import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { listSystemPrompts } from '@platform/client';
import {
  systemPromptGroupSchema,
  systemPromptsSchema,
} from '@platform/schema/generation';
import { useQuery } from '@tanstack/react-query';

import { counted } from '../library/counted';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import { PromptBlock } from './PromptBlock';

import type {
  SystemPromptAgent,
  SystemPromptGroup,
} from '@platform/schema/generation';

const HEADINGS: Record<SystemPromptGroup, string> = {
  lessons: 'Lessons',
  modules: 'Modules',
  collections: 'Courses and paths',
  images: 'Images',
  learner: 'Learner',
};

/** Every agent's current system prompt, grouped by where it is used — ADMIN-26. */
export function SystemPrompts() {
  const prompts = useQuery({
    queryKey: ['system-prompts'],
    queryFn: async () => {
      const { agents, answerRule, texts } = systemPromptsSchema.parse(
        (await listSystemPrompts({ throwOnError: true })).data,
      );
      const byDigest = new Map(texts.map((one) => [one.digest, one.text]));
      return {
        answerRule,
        agents: agents.map((agent) => ({
          agent,
          text: byDigest.get(agent.digest) ?? '',
        })),
      };
    },
  });

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader
        title="System prompts"
        subtitle="The prompts change through the skill files in the repository. A module's writer notes are edited on the module."
      />

      {prompts.isPending && <LinearProgress />}
      {prompts.isError && (
        <Alert severity="error">The system prompts could not be loaded.</Alert>
      )}

      {prompts.isSuccess && (
        <>
          <Typography variant="body2" color="text.secondary">
            Admin agents run on Claude Code, which sends its own system prompt
            before the text below, and a note on where it runs, such as the
            working directory, that no length here counts. A learner agent asked
            for a structured answer gets one more line after its text: “
            {prompts.data.answerRule}”
          </Typography>
          {systemPromptGroupSchema.options.map((group) => (
            <Stack key={group} spacing={1}>
              <Typography variant="h6" component="h2">
                {HEADINGS[group]}
              </Typography>
              {prompts.data.agents
                .filter(({ agent }) => agent.group === group)
                .map(({ agent, text }) => (
                  <AgentPrompt key={agent.agent} agent={agent} text={text} />
                ))}
            </Stack>
          ))}
        </>
      )}
    </Stack>
  );
}

function AgentPrompt({
  agent,
  text,
}: {
  agent: SystemPromptAgent;
  text: string;
}) {
  const id = `system-prompt-${agent.agent}`;

  return (
    <Accordion
      variant="outlined"
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary aria-controls={`${id}-text`} id={id}>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2">{agent.label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {agent.usedIn}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {agent.skills.length === 0
              ? 'Role prompt'
              : agent.skills.join(', ')}{' '}
            · {agent.digest}
          </Typography>
          {agent.preset !== undefined && (
            <Typography variant="caption" color="text.secondary">
              Claude Code preset,{' '}
              {counted(agent.preset.characters, 'character')}, then this agent’s
              text, {counted(text.length, 'character')}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {agent.preset !== undefined && (
            <Typography variant="body2" color="text.secondary">
              Claude Code {agent.preset.claudeCode} sends its own system prompt
              first and appends this text to it.
            </Typography>
          )}
          <PromptBlock text={text} />
          {agent.taskRules !== undefined && (
            <Stack spacing={1}>
              <Typography variant="subtitle2" component="h3">
                Task rules
              </Typography>
              <PromptBlock
                text={agent.taskRules.join('\n')}
                label="Task rules"
              />
            </Stack>
          )}
          {agent.perRequest !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {agent.perRequest}
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
